// src/utils/cookieService.js
import Cookies from 'js-cookie';

export default class CookieService {
  /**
   * Encode a string to base64 and remove padding (=) for obfuscation
   * @param {string} str - String to encode
   * @returns {string} Base64 encoded string without padding
   */
  static _encodeBase64(str) {
    try {
      // Encode to base64 and remove "=" padding to make it less obvious
      return btoa(unescape(encodeURIComponent(str))).replace(/=/g, '');
    } catch (e) {
      console.error('Failed to encode base64:', e);
      return str;
    }
  }

  /**
   * Decode a base64 string (restore padding if needed)
   * @param {string} str - Base64 string to decode (without padding)
   * @returns {string} Decoded string
   */
  static _decodeBase64(str) {
    try {
      // Restore padding if necessary (base64 length must be multiple of 4)
      let base64 = str;
      while (base64.length % 4 !== 0) {
        base64 += '=';
      }
      return decodeURIComponent(escape(atob(base64)));
    } catch (e) {
      console.error('Failed to decode base64:', e);
      return str;
    }
  }

  /**
   * Generate a random obfuscated key name
   * @param {string} key - Original key
   * @returns {string} Obfuscated key name
   */
  static _obfuscateKey(key) {
    // Create a consistent hash-like key from the original key
    // This ensures the same key always maps to the same obfuscated name
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return '_' + Math.abs(hash).toString(36);
  }

  /**
   * Set a cookie with base64 encoding for obfuscation
   * @param {string} key - Cookie key
   * @param {any} value - Value to store (will be JSON stringified if object)
   * @param {object} options - Cookie options
   */
  static set(key, value, options = {}) {
    const defaults = {
      expires: 7, // 7 days
      path: '/',
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production'
    };

    const finalOptions = { ...defaults, ...options };

    // Convert value to string if it's an object
    const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
    
    // Encode the value in base64 for obfuscation
    const encodedValue = this._encodeBase64(stringValue);
    
    // Obfuscate the key name
    const obfuscatedKey = this._obfuscateKey(key);

    Cookies.set(obfuscatedKey, encodedValue, finalOptions);
  }

  /**
   * Get a cookie value and decode it
   * @param {string} key - Cookie key
   * @param {boolean} parseJSON - Whether to parse as JSON
   * @returns {any} Cookie value
   */
  static get(key, parseJSON = false) {
    const obfuscatedKey = this._obfuscateKey(key);
    const encodedValue = Cookies.get(obfuscatedKey);
    
    if (!encodedValue) return null;

    // Decode from base64
    const decodedValue = this._decodeBase64(encodedValue);

    if (parseJSON) {
      try {
        return JSON.parse(decodedValue);
      } catch (e) {
        console.error('Failed to parse cookie JSON:', e);
        return decodedValue;
      }
    }

    return decodedValue;
  }

  /**
   * Remove a cookie
   * @param {string} key - Cookie key
   * @param {object} options - Cookie options (path, domain)
   */
  static remove(key, options = {}) {
    const defaults = { path: '/' };
    const obfuscatedKey = this._obfuscateKey(key);
    Cookies.remove(obfuscatedKey, { ...defaults, ...options });
  }

  /**
   * Check if cookie exists
   * @param {string} key - Cookie key
   * @returns {boolean}
   */
  static has(key) {
    const obfuscatedKey = this._obfuscateKey(key);
    return Cookies.get(obfuscatedKey) !== undefined;
  }

  /**
   * Get all cookies
   * @returns {object} All cookies
   */
  static getAll() {
    return Cookies.get();
  }
}